<template>
  <div class="novel-list-toolbar">
    <!-- PC版 -->
    <div class="toolbar-pc pc">
      <div class="toolbar-left">
        <div class="search-input-wrapper" :class="{ 'search-active': searchQuery }">
          <IconSearch class="search-icon" />
          <input
            type="text"
            class="search-input"
            :placeholder="$t('homePage.novelList.toolbar.searchPlaceholder')"
            :value="searchQuery"
            @input="onSearchInput"
          />
        </div>
        <button class="toolbar-icon-button" :class="{ active: hasActiveFilter }" @click="$emit('open-filter')">
          <IconSlider class="toolbar-icon-svg" />
        </button>
      </div>
      <div class="toolbar-right">
        <div class="sort-dropdown-wrapper" v-click-outside="closeSortDropdown">
          <button class="sort-dropdown-trigger" @click="toggleSortDropdown">
            <IconMenu class="sort-prefix-icon" />
            <span class="sort-label">{{ currentSortLabel }}</span>
            <IconChevronDown class="sort-chevron" />
          </button>
          <div v-if="isSortDropdownOpen" class="sort-dropdown-menu">
            <button
              v-for="option in sortOptions"
              :key="option.value"
              class="sort-dropdown-item"
              :class="{ active: sortKey === option.value }"
              @click="onSelectSort(option.value)"
            >
              {{ option.label }}
            </button>
          </div>
        </div>
        <button class="toolbar-icon-button" :disabled="!sortKey" @click="onToggleSortOrder">
          <IconOrderAsc v-if="sortOrder === 'asc'" class="toolbar-icon-svg" />
          <IconOrderDesc v-else class="toolbar-icon-svg" />
        </button>
        <button class="toolbar-icon-button" @click="$emit('update:layout', layout === 'grid' ? 'list' : 'grid')">
          <IconLayoutList v-if="layout === 'grid'" class="toolbar-icon-svg" />
          <IconLayoutGrid v-else class="toolbar-icon-svg" />
        </button>
      </div>
    </div>

    <!-- スマホ版 -->
    <div class="toolbar-sp sp">
      <!-- 検索展開時: inputが全幅に広がる -->
      <div v-if="isSpSearchOpen" class="sp-search-bar">
        <div class="search-input-wrapper" :class="{ 'search-active': searchQuery }">
          <IconSearch class="search-icon" />
          <input
            ref="spSearchInput"
            type="text"
            class="search-input"
            :placeholder="$t('homePage.novelList.toolbar.searchPlaceholder')"
            :value="searchQuery"
            @input="onSearchInput"
            @blur="onSpSearchBlur"
          />
          <button class="sp-search-close" @mousedown.prevent @click="onSpSearchClose" />
        </div>
      </div>

      <!-- 通常時: アイコンツールバー -->
      <template v-else>
        <div class="toolbar-left">
          <button class="toolbar-icon-button" :class="{ active: searchQuery }" @click="toggleSpSearch">
            <IconSearch class="toolbar-icon-svg" />
          </button>
          <button class="toolbar-icon-button" :class="{ active: hasActiveFilter }" @click="$emit('open-filter')">
            <IconSlider class="toolbar-icon-svg" />
          </button>
        </div>
        <div class="toolbar-right">
          <button class="toolbar-icon-button" :class="{ active: sortKey }" @click="toggleSpSort">
            <IconMenu class="toolbar-icon-svg" />
          </button>
          <button class="toolbar-icon-button" @click="$emit('update:layout', layout === 'grid' ? 'list' : 'grid')">
            <IconLayoutList v-if="layout === 'grid'" class="toolbar-icon-svg" />
            <IconLayoutGrid v-else class="toolbar-icon-svg" />
          </button>
        </div>
      </template>

    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import { Dialog } from "@/lib/utils/dialog";
import NovelSortDialog from "@/components/ui/dialogs/NovelSortDialog.vue";
import IconChevronDown from "~icons/figma/ChevronDown";
import IconSearch from "~icons/figma/Search";
import IconSlider from "~icons/figma/Slider";
import IconOrderAsc from "~icons/figma/OrderAsc";
import IconOrderDesc from "~icons/figma/OrderDesc";
import IconLayoutList from "~icons/figma/LayoutList";
import IconLayoutGrid from "~icons/figma/LayoutGrid";
import IconMenu from "~icons/figma/Menu";

const ClickOutside = require("vue-click-outside");

interface SortOption {
  value: string;
  label: string;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    IconChevronDown,
    IconSearch,
    IconSlider,
    IconOrderAsc,
    IconOrderDesc,
    IconLayoutList,
    IconLayoutGrid,
    IconMenu,
  },
  directives: {
    ClickOutside,
  },
  props: {
    searchQuery: {
      type: String,
      default: "",
    },
    sortKey: {
      type: String,
      default: "",
    },
    sortOrder: {
      type: String,
      default: "asc",
    },
    layout: {
      type: String,
      default: "grid",
    },
    hasActiveFilter: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isSpSearchOpen: false,
      isSortDropdownOpen: false,
    };
  },
  computed: {
    sortOptions() {
      return [
        { value: "createdAt", label: this.$t("homePage.novelList.toolbar.sortCreatedAt") as string },
        { value: "updatedAt", label: this.$t("homePage.novelList.toolbar.sortUpdatedAt") as string },
        { value: "title", label: this.$t("homePage.novelList.toolbar.sortTitle") as string },
        { value: "", label: this.$t("homePage.novelList.toolbar.sortNone") as string },
      ];
    },
    currentSortLabel() {
      const option = this.sortOptions.find((o) => o.value === this.sortKey);
      return option ? option.label : (this.$t("homePage.novelList.toolbar.sortNone") as string);
    },
  },
  methods: {
    onSearchInput(event: Event) {
      const target = event.target as HTMLInputElement;
      this.$emit("update:searchQuery", target.value);
    },
    onToggleSortOrder() {
      this.$emit("update:sortOrder", this.sortOrder === "asc" ? "desc" : "asc");
    },
    toggleSortDropdown() {
      this.isSortDropdownOpen = !this.isSortDropdownOpen;
    },
    closeSortDropdown() {
      this.isSortDropdownOpen = false;
    },
    onSelectSort(value: string) {
      this.$emit("update:sortKey", value);
      this.isSortDropdownOpen = false;
    },
    toggleSpSearch() {
      this.isSpSearchOpen = !this.isSpSearchOpen;
      if (this.isSpSearchOpen) {
        this.$nextTick(() => {
          const input = this.$refs.spSearchInput as HTMLInputElement;
          if (input) input.focus();
        });
      } else {
        this.$emit("update:searchQuery", "");
      }
    },
    onSpSearchBlur() {
      this.isSpSearchOpen = false;
    },
    onSpSearchClose() {
      if (this.searchQuery) {
        this.$emit("update:searchQuery", "");
      } else {
        this.isSpSearchOpen = false;
      }
    },
    async toggleSpSort() {
      this.isSpSearchOpen = false;
      const sortDialog = new Dialog(NovelSortDialog);
      await sortDialog.show({
        sortKey: this.sortKey,
        sortOrder: this.sortOrder,
        onChange: (result: { sortKey: string; sortOrder: string }) => {
          this.$emit("update:sortKey", result.sortKey);
          this.$emit("update:sortOrder", result.sortOrder);
        },
      });
    },
  },
});

interface Props {
  searchQuery: string;
  sortKey: string;
  sortOrder: string;
  layout: string;
  hasActiveFilter: boolean;
}

interface Data {
  isSpSearchOpen: boolean;
  isSortDropdownOpen: boolean;
}

interface Computed {
  sortOptions: SortOption[];
  currentSortLabel: string;
}

interface Methods {
  onSearchInput(event: Event): void;
  onToggleSortOrder(): void;
  toggleSortDropdown(): void;
  closeSortDropdown(): void;
  onSelectSort(value: string): void;
  toggleSpSearch(): void;
  onSpSearchBlur(): void;
  onSpSearchClose(): void;
  toggleSpSort(): void;
}
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.sp {
  display: none !important;
}
@media only screen and (max-width: 750px) {
  .pc {
    display: none !important;
  }
  .sp {
    display: block !important;
  }
}

.novel-list-toolbar {
  padding: 12px 0;

}

/* SVG アイコン共通 */
.toolbar-icon-svg {
  font-size: var(--font-size-lg);
  flex-shrink: 0;
}

/* ================================
   PC版ツールバー
   ================================ */
.toolbar-pc {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.toolbar-left,
.toolbar-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

/* 検索input */
.search-input-wrapper {
  display: flex;
  align-items: center;
  background-color: var(--color-surface-base);
  border-radius: var(--radius-sm);
  padding: 0 12px;
  height: 34px;
  box-sizing: border-box;
  min-width: 220px;

  .search-icon {
    font-size: var(--font-size-md);
    flex-shrink: 0;
    color: var(--color-text-low);
  }

  &.search-active .search-icon {
    color: var(--color-primary);
  }

  .search-input {
    border: none;
    background: transparent;
    outline: none;
    font-size: var(--font-size-xs);
    line-height: 34px;
    color: var(--color-text-normal);
    width: 100%;
    margin-left: 8px;
    padding: 0;

    @include mq("sp") {
      font-size: var(--font-size-md);
    }

    &::placeholder {
      color: var(--color-text-low);
    }
  }
}

/* アイコンボタン共通 */
.toolbar-icon-button {
  position: relative;
  @include hover-overlay;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: none;
  border-radius: var(--radius-rounded);
  background: var(--color-border);
  color: var(--color-text-normal);
  cursor: pointer;
  flex-shrink: 0;

  &.active {
    background-color: var(--color-surface-hover);
    color: var(--color-primary);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;

    &::after {
      background: transparent;
    }
  }
}

/* ソートドロップダウン */
.sort-dropdown-wrapper {
  position: relative;
}

.sort-dropdown-trigger {
  position: relative;
  @include hover-overlay;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 10px;
  height: 34px;
  background: var(--color-surface-base);
  border: none;
  border-radius: var(--radius-sm);
  cursor: pointer;
  color: var(--color-text-normal);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  line-height: 34px;
  white-space: nowrap;
  transition: background-color 0.15s;


  .sort-prefix-icon {
    font-size: var(--font-size-lg);
    flex-shrink: 0;
  }

  .sort-chevron {
    font-size: var(--font-size-md);
    color: var(--color-text-low);
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
  }
}

.sort-dropdown-menu {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  min-width: 100%;
  background: var(--color-background-normal);
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-md);
  z-index: 10;
  overflow: hidden;
}

.sort-dropdown-item {
  position: relative;
  @include hover-overlay;
  display: block;
  width: 100%;
  padding: 8px 14px;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-normal);
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
  white-space: nowrap;

  &.active {
    background-color: var(--color-surface-base);
    font-weight: var(--font-weight-bold);
  }
}

/* ================================
   スマホ版ツールバー
   ================================ */
.toolbar-sp {
  position: relative;

  > .toolbar-left,
  > .toolbar-right {
    display: inline-flex;
    gap: 8px;
  }

  > .toolbar-left {
    float: left;
  }

  > .toolbar-right {
    float: right;
  }

  &::after {
    content: "";
    display: table;
    clear: both;
  }
}

/* スマホ版: 検索バー（全幅展開） */
.sp-search-bar {
  .search-input-wrapper {
    width: 100%;
    min-width: 0;
  }
}

.sp-search-close {
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-surface-base);
  border: none;
  border-radius: var(--radius-rounded);
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  position: relative;

  &::before,
  &::after {
    content: "";
    position: absolute;
    width: 10px;
    height: 1.5px;
    background-color: var(--color-inactive);
    border-radius: 1px;
  }

  &::before {
    transform: rotate(45deg);
  }

  &::after {
    transform: rotate(-45deg);
  }
}

</style>
